import {
  convertEventSelectToTarget,
  DataTable,
  FormFeedBack,
  IOptions,
  ModalConfirmAction,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import {
  AddPersonToGroupUseCaseInterface,
  GetAllPersonsFromGroupUseCaseInterface,
  GetAllProfessionalsNotPaginatedUseCaseInterface,
  ImportPersonsFromAnotherGroupUseCaseInterface,
  RemovePersonFromGroupUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedGroupPreceptorsListData,
  loadSelectedGroupPreceptorsListData,
} from 'presentation/config/store/groups/group-settings-slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, CardBody, Col, Form, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsGroupSettingsPersonsFromGroupList } from '../../../common/columns/group-settings/columns-group-settings-persons-from-group-list';
import { ImportEntitiesFromAnotherGroupModal } from '../import-entities-from-another-group-modal';

interface IProps {
  groupId: string;
}

const statusInitialStates = {
  confirmRemovalFromGroupModalBtnsStatus: STATUSPAGE.DONE,
  importPreceptorsFromAnotherGroupModalBtnsStatus: STATUSPAGE.DONE,
  preceptorRemoteSelectInputStatus: STATUSPAGE.DONE,
  addPreceptorToGroupBtnStatus: STATUSPAGE.DONE,
  preceptorsFromGroupListStatus: STATUSPAGE.DONE,
};

export const PreceptorsTabContent = ({ groupId }: IProps) => {
  // Hooks
  const { professionalService, groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const [
    isConfirmRemovalFromGroupModalOpen,
    setIsConfirmRemovalFromGroupModalOpen,
  ] = useState(false);
  const [
    isImportPreceptorsFromAnotherGroupModalOpen,
    setIsImportPreceptorsFromAnotherGroupModalOpen,
  ] = useState(false);
  const [
    selectedPreceptorFromGroupRelationId,
    setSelectedPreceptorFromGroupRelationId,
  ] = useState<string | null>(null);
  const [statusStates, setStatusStates] = useState(statusInitialStates);
  const { selectedGroupPreceptorsListData } = useAppSelector(
    (state) => state.groupSettings,
  );
  const [preceptorSelectOptions, setPreceptorSelectOptions] =
    useState<GetAllProfessionalsNotPaginatedUseCaseInterface.Result>(null);
  const preceptorOptions: IOptions[] = useMemo(() => {
    return (
      preceptorSelectOptions?.data?.map((preceptor) => {
        const { cpf, name, id } = preceptor;
        return {
          value: id,
          label: `${cpf} - ${name}`,
        };
      }) || []
    );
  }, [preceptorSelectOptions]);

  // Methods
  const changeStatusState = useCallback(
    <T extends keyof typeof statusInitialStates>(key: T, value: STATUSPAGE) => {
      setStatusStates((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const toggleConfirmRemovalFromGroupModal = useCallback(
    () => setIsConfirmRemovalFromGroupModalOpen((prevState) => !prevState),
    [],
  );

  const toggleImportPreceptorsFromAnotherGroupModal = useCallback(
    () =>
      setIsImportPreceptorsFromAnotherGroupModalOpen((prevState) => !prevState),
    [],
  );

  const requestGetAllPreceptorsFromGroup = useCallback(
    async (params: GetAllPersonsFromGroupUseCaseInterface.Params) => {
      changeStatusState('preceptorsFromGroupListStatus', STATUSPAGE.LOADING);
      await groupService
        .getAllPersonsFromGroup(params)
        .then((res) => dispatch(loadSelectedGroupPreceptorsListData(res)))
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearSelectedGroupPreceptorsListData());
        })
        .finally(() =>
          changeStatusState('preceptorsFromGroupListStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestGetAllPreceptorsForSelectOptions = useCallback(
    async (params: GetAllProfessionalsNotPaginatedUseCaseInterface.Params) => {
      changeStatusState('preceptorRemoteSelectInputStatus', STATUSPAGE.LOADING);
      await professionalService
        .getAllProfessionalsNotPaginated(params)
        .then((res) => setPreceptorSelectOptions(res))
        .catch((err) => {
          notifyError(err.message);
          setPreceptorSelectOptions(null);
        })
        .finally(() =>
          changeStatusState(
            'preceptorRemoteSelectInputStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestAddPreceptorToGroup = useCallback(
    async (params: AddPersonToGroupUseCaseInterface.Params) => {
      changeStatusState('addPreceptorToGroupBtnStatus', STATUSPAGE.LOADING);
      await groupService
        .addPersonToGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllPreceptorsFromGroup({
            groupId,
            personType: PROFILE_TYPE.PRECEPTOR,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState('addPreceptorToGroupBtnStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestRemovePreceptorFromGroup = useCallback(
    async (params: RemovePersonFromGroupUseCaseInterface.Params) => {
      changeStatusState(
        'confirmRemovalFromGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .removePersonFromGroup(params)
        .then((res) => notifySuccess(res.message))
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'confirmRemovalFromGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestImportPreceptorsFromAnotherGroup = useCallback(
    async (params: ImportPersonsFromAnotherGroupUseCaseInterface.Params) => {
      changeStatusState(
        'importPreceptorsFromAnotherGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .importPersonsFromAnotherGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllPreceptorsFromGroup({
            groupId,
            personType: PROFILE_TYPE.PRECEPTOR,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'importPreceptorsFromAnotherGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      preceptor: null,
    },

    validationSchema: Yup.object({
      preceptor: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        id: values.preceptor?.value,
        group: groupId,
        personType: PROFILE_TYPE.PRECEPTOR,
      };

      requestAddPreceptorToGroup(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllPreceptorsFromGroup({
      groupId,
      personType: PROFILE_TYPE.PRECEPTOR,
    });
  }, [groupId]);

  return (
    <>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <CardBody>
          <Row>
            <Col lg={7} className="mb-3">
              <SelectRemote
                isClearable
                name="preceptor"
                clearOptions={() => setPreceptorSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setPreceptorSelectOptions(null);
                    return;
                  }

                  if (searchString.length < 3) return;

                  requestGetAllPreceptorsForSelectOptions({
                    profileType: PROFILE_TYPE.PRECEPTOR,
                    query: searchString,
                  });
                }}
                inputId={'preceptor'}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('preceptor', e),
                  );
                }}
                onBlur={(e) => {
                  setPreceptorSelectOptions(null);
                }}
                options={preceptorOptions}
                placeholder={`Selecione um ${getLegends('PRECEPTOR')}`}
                value={validation.values.preceptor}
                loading={
                  statusStates.preceptorRemoteSelectInputStatus ===
                  STATUSPAGE.LOADING
                }
                isInvalid={
                  !!(
                    validation.touched?.preceptor &&
                    validation.errors?.preceptor
                  )
                }
                isDisabled={
                  statusStates.addPreceptorToGroupBtnStatus ===
                  STATUSPAGE.LOADING
                }
              />
              <FormFeedBack
                error={String(validation.errors?.preceptor)}
                isInvalid={
                  !!(
                    validation.touched?.preceptor &&
                    validation.errors?.preceptor
                  )
                }
              />
            </Col>
            <Col lg={5} className="mb-3">
              <div className="d-flex justify-content-end gap-4">
                <Button
                  type="submit"
                  disabled={
                    statusStates.addPreceptorToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  {statusStates.addPreceptorToGroupBtnStatus ===
                    STATUSPAGE.LOADING && (
                    <Spinner size="sm">{['Carregando...']}</Spinner>
                  )}
                  Adicionar
                </Button>
                <Button
                  type="button"
                  onClick={() => toggleImportPreceptorsFromAnotherGroupModal()}
                  disabled={
                    statusStates.addPreceptorToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  Importar de outro {getLegends('GRUPO')}
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <DataTable
              noDataBg={noDataBg}
              actions={[
                {
                  id: 1,
                  name: 'remove',
                  label: 'Remover',
                  icon: 'ri-indeterminate-circle-fill',
                  classNameColorIcon: 'danger',
                  onPress: (cellProps) => {
                    setSelectedPreceptorFromGroupRelationId(
                      cellProps.relationId,
                    );
                    toggleConfirmRemovalFromGroupModal();
                  },
                },
              ]}
              canSearch
              columns={makeColumnsGroupSettingsPersonsFromGroupList()}
              data={selectedGroupPreceptorsListData}
              loading={
                statusStates.preceptorsFromGroupListStatus ===
                STATUSPAGE.LOADING
              }
            />
          </Row>
        </CardBody>
      </Form>

      <ModalConfirmAction
        modalTheme="danger"
        title="Deseja remover?"
        actionBtnLabel="Remover"
        isOpen={isConfirmRemovalFromGroupModalOpen}
        toggle={toggleConfirmRemovalFromGroupModal}
        onConfirm={() =>
          requestRemovePreceptorFromGroup({
            relationId: selectedPreceptorFromGroupRelationId,
          })
        }
        loading={
          statusStates.confirmRemovalFromGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
      />

      <ImportEntitiesFromAnotherGroupModal
        isOpen={isImportPreceptorsFromAnotherGroupModalOpen}
        toggle={toggleImportPreceptorsFromAnotherGroupModal}
        importedEntitiesTitle={getLegends('PRECEPTOR', true)}
        buttonsLoading={
          statusStates.importPreceptorsFromAnotherGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
        requestImportEntitiesFromAnotherGroup={
          requestImportPreceptorsFromAnotherGroup
        }
        importedPersonType={PROFILE_TYPE.PRECEPTOR}
        targetGroupId={groupId}
      />
    </>
  );
};
